import dates from 'src/utils/dates'
import format from 'src/utils/format'
import gam from 'src/utils/gam'

import Teams from './teams'

const org = {
  data: {
    oao: { associationId: 0, companyName: 'OAO' },
    misconfigured: { publisherId: null, billingCode: 'Misconfigured' },
    unknown: { publisherId: null, billingCode: 'Unknown' },
  },
  defaults: {
    billing_code: null,
    client_name: null,
    is_administrative: false,
  },

  isValidOrgProperty(propertyName, rawValue) {
    const isDefined = (value) => value !== undefined
    const isBoolean = (value) => typeof value === 'boolean'

    const validationRules = {
      hasCustomAnalytics: isDefined,
      hasDiscrepancy: isBoolean,
      hasPacing: isDefined,
      hasHealthAdx: isBoolean,
      hasHealthNetwork: isBoolean,
      hasProgrammatic: isBoolean,
      hasTeam: isBoolean,
      hasYieldPartner: isBoolean,
      isTimesheetEnabled: isDefined,
      isAdministrative: isDefined,
    }

    return validationRules[propertyName](rawValue)
  },
  formatOrgProperties(propertyName, rawValue) {
    const isValidValue = this.isValidOrgProperty(propertyName, rawValue)

    return isValidValue ? format.titlecase(rawValue) : 'N/A'
  },

  makePublisherIntoTeam(publisher) {
    const team = { teamCode: '-1', teamId: '-1', teamName: publisher.orgName }
    return [publisher].map(() => Teams.mapTeamOption(publisher, team))
  },
  /**
   * Adds an "All Access" team to the beginning of the teams array, then returns the new teams array.
   * @param {Organization} publisher
   * @returns {Promise<Object[]>} The publisher's teams plus an "All Access" team to represent access to all teams.
   */
  async makeTeamsFromApiResponse(publisher) {
    /* eslint-disable-next-line prefer-destructuring */
    const teams = publisher.teams
    const apiTeamsResponse = await teams

    // @NOTE: This fallback is for when we get the data from the store instead of the API. Refactors TBD.
    const { content: apiTeams = apiTeamsResponse, error } = apiTeamsResponse || {}
    const allAccess = this.makePublisherIntoTeam(publisher)

    if (!error) {
      const newTeams = apiTeams.map((team) => Teams.mapTeamOption(publisher, team))

      newTeams.unshift(...allAccess)

      return newTeams
    }

    return allAccess
  },
  /**
   * Normalizes an organization's teams array from an API response, using the organization if no teams are found.
   * PublishersAsTeams seed several endpoints' search options.
   * @see module:src/tools/store/getters~publishersAsTeams
   * @param {Organization} publisher
   * @returns {Promise<Team[]>}
   */
  makePublisherTeams(publisher) {
    const makePublisherIntoTeam = () => this.makePublisherIntoTeam(publisher)
    const makeTeamsFromApiResponse = () => this.makeTeamsFromApiResponse(publisher)

    return publisher.hasTeam && publisher.teams ? makeTeamsFromApiResponse() : makePublisherIntoTeam()
  },

  getPrimaryAlias(aliases) {
    const getPrimaryAliasConfig = () => aliases.find((alias) => alias.primary && alias.active) || aliases[0]
    const primaryAliasConfig = Array.isArray(aliases) && getPrimaryAliasConfig()
    return primaryAliasConfig && primaryAliasConfig.value
  },
  getOrgName(aliases, client, publisher) {
    const defaultOrgName = 'Name Unknown'
    return this.getPrimaryAlias(aliases) || client.clientName || publisher.name || defaultOrgName
  },

  hasDiscrepancyConfig(hasDiscrepancy, hasInternalConfig, permissions) {
    const hasDiscrepancyPermissions = () => {
      const truthyValues = [true, undefined]
      return truthyValues.indexOf(permissions?.discrepancy) !== -1
    }
    return hasDiscrepancy && (hasInternalConfig || hasDiscrepancyPermissions())
  },
  hasPacingConfig(hasPacing, hasInternalConfig, permissions) {
    const hasPacingPermissions = () => {
      const truthyValues = [true, undefined]
      return truthyValues.indexOf(permissions?.pacing) !== -1
    }
    return hasPacing && (hasInternalConfig || hasPacingPermissions())
  },

  hasCustomAnalyticsConfig(permissions) {
    return Array.isArray(permissions?.customAnalytics)
  },
  mapCustomAnalytic(orgName, orgCode, networkCode) {
    return (customAnalytic) => {
      customAnalytic.label = `${orgName} ${customAnalytic.name}`

      customAnalytic.billingCode = orgCode
      customAnalytic.networkCode = networkCode
      customAnalytic.teamId = null

      customAnalytic.baseReportName = customAnalytic.href
      customAnalytic.routeName = 'custom-analytics'

      customAnalytic.associationId = orgCode
      customAnalytic.subId = null

      return customAnalytic
    }
  },
  getCustomAnalytics(orgName, orgCode, networkCode, permissions) {
    return permissions.customAnalytics.map(this.mapCustomAnalytic(orgName, orgCode, networkCode))
  },
  getCustomAnalyticsArray(hasCustomAnalytics, orgName, orgCode, networkCode, permissions) {
    return (hasCustomAnalytics && this.getCustomAnalytics(orgName, orgCode, networkCode, permissions)) || []
  },

  async mapClientAndPublisher(client = {}, publisher = {}, config = {}, projects = []) {
    // Performance requires avoiding destructuring on these large objects.
    /* eslint-disable-next-line prefer-destructuring */
    const clientId = client.clientId
    /* eslint-disable-next-line prefer-destructuring */
    const publisherId = publisher.publisherId
    /* eslint-disable-next-line prefer-destructuring */
    const networkCode = publisher.networkCode

    const settings = config.settings || { reports: [] }
    const { aliases } = settings

    const orgName = this.getOrgName(aliases, client, publisher)
    const foundIdMismatch = clientId && publisherId && clientId !== publisherId
    const orgId = publisherId || -10
    const orgCode = client.billingCode || publisher.billingCode

    const isPublisher = publisher && publisher.publisherId
    const hasPacing = isPublisher && !!publisher.billingCode
    const isTimesheetEnabled = !!client.billingCode
    const isTeamEnabled = publisher.hasTeam || false

    const reportSetting = settings.reports || settings.filters
    const reports = (Array.isArray(reportSetting) && reportSetting) || []

    const { expires, permissions = {} } = config.access || {}
    const hasAccessExpired = expires && dates.areDatesGte(expires, dates.now())
    const hasInternalConfig = config.isInternal

    const hasPacingConfig = this.hasPacingConfig(hasPacing, hasInternalConfig, permissions)
    const hasDiscrepancyConfig = this.hasDiscrepancyConfig(publisher.hasDiscrepancy, hasInternalConfig, permissions)
    const hasCustomAnalytics = this.hasCustomAnalyticsConfig(permissions)

    const hasCustomAnalyticsView = this.formatOrgProperties('hasCustomAnalytics', hasCustomAnalytics)
    const hasPacingReport = this.formatOrgProperties('hasPacing', hasPacingConfig)
    const hasTimesheetReport = this.formatOrgProperties('isTimesheetEnabled', isTimesheetEnabled)
    const hasDiscrepancyReport = this.formatOrgProperties('hasDiscrepancy', hasDiscrepancyConfig)
    const hasHealthAdxReport = this.formatOrgProperties('hasHealthAdx', publisher.hasHealthAdx)
    const hasHealthNetworkReport = this.formatOrgProperties('hasHealthNetwork', publisher.hasHealthNetwork)
    const hasYieldPartnerReport = this.formatOrgProperties('hasYieldPartner', publisher.hasYieldPartner)
    const isInternalUseOnly = this.formatOrgProperties('isAdministrative', client.isAdministrative)

    const customAnalytics = this.getCustomAnalyticsArray(hasCustomAnalytics, orgName, orgCode, networkCode, permissions)
    const customAnalyticsActive = customAnalytics.filter((customAnalytic) => customAnalytic.active)

    // The API response supersedes the Store configuration: config < publisher < client.
    // Client supersedes publisher, and they both supersede the organization config.
    const item = { ...config, ...publisher, ...client }
    const validNetworkCode = networkCode && Number(networkCode) >= 0
    const hasProjects = Array.isArray(projects) && projects.find((project) => project.projectCode === orgCode)

    item.associationId = orgCode
    item.subId = '-1'

    item.baseReportName = 'organization'
    item.reportPath = 'profile'
    item.routeName = 'internal'

    item.dataType = 'organization'

    item.adServerUrl = validNetworkCode ? gam.network(networkCode) : null
    item.foundIdMismatch = foundIdMismatch
    item.hasAccessExpired = hasAccessExpired
    item.hasCustomAnalytics = hasCustomAnalytics
    // @TODO Rename hasCustomAnalyticsView to s/t that represents it's for printing.
    item.hasCustomAnalyticsView = hasCustomAnalyticsView
    item.hasDiscrepancy = hasDiscrepancyConfig
    item.hasDiscrepancyReport = hasDiscrepancyReport
    item.hasPacing = hasPacingConfig
    item.hasPacingReport = hasPacingReport
    item.hasProjects = hasProjects
    item.hasHealthAdxReport = hasHealthAdxReport
    item.hasHealthNetworkReport = hasHealthNetworkReport
    // item.hasProgrammatic = hasProgrammatic // inherited
    // item.hasYieldPartner = hasYieldPartner // inherited
    item.hasYieldPartnerReport = hasYieldPartnerReport
    item.hasTeam = publisher.hasTeam || false
    item.hasTimesheetReport = hasTimesheetReport
    item.href = `/internal/organization/${orgCode}`
    item.isInternalUseOnly = isInternalUseOnly
    item.isPublisher = isPublisher
    item.isTeamEnabled = isTeamEnabled
    item.isTimesheetEnabled = isTimesheetEnabled
    item.orgCode = orgCode
    item.orgId = orgId // @TODO Perhaps rename orgId to orgTableId.
    item.orgName = orgName

    item.customAnalytics = customAnalytics
    item.customAnalyticsActive = customAnalyticsActive

    item.operator = 'incl'
    item.text = orgName
    item.value = orgCode

    item.settings = { reports }

    item.teams = await this.makePublisherTeams(item)

    return item
  },
  async reduceClientsAndPublishers(orgConfigs, orgProjects, orgsArrayPromise, clientOrPublisher, index, clientsAndPublishers) {
    const orgsArray = await orgsArrayPromise
    const { billingCode } = clientOrPublisher
    const inArray = orgsArray.find((o) => o.billingCode === billingCode)

    if (inArray) return orgsArray

    const matches = clientsAndPublishers.filter((p) => p.billingCode === billingCode)

    if (!matches?.length) return orgsArray

    const foundOrgConfig = orgConfigs.find((orgConfig) => orgConfig?.docId === billingCode)
    const makeDefaultConfig = () => ({ access: {}, isInternal: (orgConfigs[0] || {}).isInternal, settings: {} })
    const config = foundOrgConfig || makeDefaultConfig()

    if (matches.length > 1) {
      const client = matches[0].clientId ? matches[0] : matches[1]
      const publisher = matches[0].publisherId ? matches[0] : matches[1]
      const projects = orgProjects.filter((project) => project.billingCode === client.billingCode)
      const result = await this.mapClientAndPublisher(client, publisher, config, projects)

      orgsArray.push(result)
    } else {
      const client = matches[0].clientId ? matches[0] : {}
      const publisher = matches[0].publisherId ? matches[0] : {}
      const projects = orgProjects.filter((project) => project.billingCode === client.billingCode)
      const result = await this.mapClientAndPublisher(client, publisher, config, projects)

      orgsArray.push(result)
    }

    return orgsArray
  },

  hasPropLength(prop) {
    return String(prop).length > 0
  },
  validateOrganization(organization) {
    const { code, id, networkCode } = organization
    return this.hasPropLength(id) && this.hasPropLength(code) && this.hasPropLength(networkCode)
  },
  validateOneTeam(team) {
    const { code, id } = team
    return this.hasPropLength(code) && this.hasPropLength(id)
  },
  validateNetworkRecords(assignedNetworks) {
    if (!Array.isArray(assignedNetworks)) return false

    const [primaryNetwork] = assignedNetworks

    if (!primaryNetwork) return false

    const { organization, team } = primaryNetwork
    const hasValidOrgStructure = organization
      && String(organization.id).length > 0
      && ['number', 'string'].includes(typeof organization.id)
    const isValidOrganization = hasValidOrgStructure && this.validateOrganization(organization)
    const isValidTeam = Array.isArray(team) && team.length > 0 && this.validateOneTeam(team[0])

    if (!isValidOrganization || !isValidTeam) return false

    return true
  },

  flattenUserNetworks(preparedNetworks) {
    const networkSeed = {
      billingCodes: [],
      orgIds: [],
      orgNames: [],
      reportCodes: [],
      teamCodes: [],
      teamIds: [],
      teamNames: [],
    }
    return preparedNetworks.reduce((networkData, preparedNetwork) => {
      const { publisher } = preparedNetwork
      const setPublisher = () => {
        const { billingCode, orgId, orgName, teamCode, teamId, teamName } = publisher

        networkData.billingCodes.push(billingCode)
        networkData.orgIds.push(orgId)
        networkData.orgNames.push(orgName)

        networkData.reportCodes.push(`${billingCode}/${teamId}`)
        networkData.teamCodes.push(`${billingCode}/${teamCode}`)
        networkData.teamIds.push(`${orgId}/${teamId}`)
        networkData.teamNames.push(teamName)
      }

      if (publisher) setPublisher()

      return networkData
    }, networkSeed)
  },
}

export default org
